const URL_REGEX = /https?:\/\/\S+/gi;
const HASHTAG_REGEX = /#\w+/g;

const stripBulletPrefix = (line: string): string =>
  line.replace(/^[•*-]\s*/, "").trim();

const collapseWhitespace = (text: string): string =>
  text.replace(/\s+/g, " ").trim();

const truncateAtWord = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  const trimmed = text.slice(0, Math.max(maxLength - 1, 0)).trim();
  const lastSpace = trimmed.lastIndexOf(" ");
  const safe = lastSpace > 60 ? trimmed.slice(0, lastSpace) : trimmed;
  return `${safe.trimEnd()}…`;
};

export const normalizeSocialDraftText = (text: string): string => {
  if (!text) return "";

  let normalized = text
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t")
    .replace(/\r\n/g, "\n")
    .trim();
  normalized = normalized.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n");

  const inlineBulletCount = (normalized.match(/\s-\s+/g) || []).length;
  const hasInlineBulletList =
    inlineBulletCount >= 2 && !/\n[•*-]\s/.test(normalized);

  if (hasInlineBulletList) {
    normalized = normalized.replace(/:\s*-\s+/g, ":\n• ");
    normalized = normalized.replace(/\s-\s+/g, "\n• ");
  }

  normalized = normalized.replace(/\n[-*]\s+/g, "\n• ");
  normalized = normalized.replace(
    /\n•\s*(Plus,|Also,|Additionally,|Don['’]t|Ready to|Learn more|Sign up|Join\b|Get started)/g,
    "\n\n$1"
  );
  normalized = normalized.replace(/([.!?])\s+(https?:\/\/\S+)/g, "$1\n\n$2");

  return normalized.trim();
};

export const deriveTwitterDraft = (
  text: string,
  maxCharacters: number = 280
): string => {
  const normalized = normalizeSocialDraftText(text);
  if (!normalized) return "";

  const uniqueHashtags = Array.from(new Set(normalized.match(HASHTAG_REGEX) || [])).slice(0, 2);
  const urls = normalized.match(URL_REGEX) || [];
  const primaryUrl = urls[0] || "";

  const lines = normalized
    .split(/\n+/)
    .map(stripBulletPrefix)
    .map((line) => line.replace(URL_REGEX, "").replace(HASHTAG_REGEX, ""))
    .map(collapseWhitespace)
    .filter(Boolean);

  const suffixParts = [
    uniqueHashtags.length ? uniqueHashtags.join(" ") : "",
    primaryUrl,
  ].filter(Boolean);
  const suffix = suffixParts.join(" ");
  const available = suffix ? maxCharacters - suffix.length - 1 : maxCharacters;

  if (available <= 0) {
    return suffix.slice(0, maxCharacters);
  }

  let draft = "";
  for (const line of lines) {
    const candidate = draft ? `${draft} ${line}` : line;
    if (candidate.length <= available) {
      draft = candidate;
      continue;
    }
    if (!draft) {
      draft = truncateAtWord(line, available);
    }
    break;
  }

  if (!draft) {
    draft = truncateAtWord(
      collapseWhitespace(
        normalized.replace(URL_REGEX, "").replace(HASHTAG_REGEX, "")
      ),
      available
    );
  }

  return [draft, suffix].filter(Boolean).join(" ").trim();
};
